import { useCallback, useState } from 'react';
import { ADMIN_AUTH_ERROR } from '@/contracts';

import { useAdminSession } from '@/app/admin-session';
import { adminApi } from '@/lib/api';
import { INITIAL_LOGIN_STATE, type AdminLoginState } from '@/lib/admin-login-state';
import { adminSession } from '@/lib/session';

/**
 * The two-step sign-in, as a hook.
 *
 * This is what the server action was, and the flow it drives is unchanged
 * because the flow is the API's: `login` answers with a **challenge, never a
 * session** — there is no request that produces an access token without a TOTP
 * code, which is what makes the second factor mandatory in the system rather
 * than in this form (ADR-007).
 *
 * What genuinely changed is where the token lands. The action used to set an
 * httpOnly cookie itself; now the API sets the refresh half and this hands the
 * access half to `adminSession`, which keeps it in memory. Nothing here touches
 * storage.
 */
export function useLogin() {
  const [state, setState] = useState<AdminLoginState>(INITIAL_LOGIN_STATE);
  const [pending, setPending] = useState(false);
  const { signIn } = useAdminSession();

  const submitPassword = useCallback(
    async (email: string, password: string) => {
      setPending(true);
      const result = await adminApi.login(email, password);
      setPending(false);

      if (!result.ok) {
        setState({
          step: 'password',
          error: result.error,
          ...(result.retryAfterSeconds ? { retryAfterSeconds: result.retryAfterSeconds } : {}),
        });
        return;
      }

      // An admin who has turned two-factor off is signed in by the password step
      // itself: the API answered with a session and set the refresh cookie, so
      // adopt it exactly as the TOTP step would and skip the second factor.
      if ('accessToken' in result.data) {
        adminSession.start(result.data);
        await signIn();
        return;
      }

      const challenge = result.data;

      // An admin who has never enrolled is handed a secret *with* the challenge and
      // enrols by answering it. There is no "enrol later" state to be in, which is
      // the point — see the admin realm's own note on mandatory enrolment.
      setState(
        challenge.totpEnrolled
          ? { step: 'totp', challengeToken: challenge.challengeToken }
          : {
              step: 'enrol',
              challengeToken: challenge.challengeToken,
              ...(challenge.enrolment ? { totpSecret: challenge.enrolment.secret } : {}),
              ...(challenge.enrolment ? { totpUri: challenge.enrolment.otpauthUri } : {}),
            },
      );
    },
    [signIn],
  );

  const submitCode = useCallback(
    async (code: string) => {
      if (state.step === 'password') return;

      setPending(true);
      const result = await adminApi.verifyTotp(state.challengeToken, code);

      if (!result.ok) {
        setPending(false);

        // A challenge token is short-lived and single-use. When it is the thing
        // that expired, sending the admin back to the code field would be asking
        // them to answer a question nobody is asking any more.
        setState(
          result.error === ADMIN_AUTH_ERROR.CHALLENGE_INVALID
            ? { step: 'password', error: result.error }
            : { ...state, error: result.error },
        );
        return;
      }

      // The token is in memory from here; `signIn` reads the identity that
      // decides what the console renders.
      adminSession.start(result.data);
      await signIn();
      setPending(false);
    },
    [state, signIn],
  );

  return { state, pending, submitPassword, submitCode };
}
